/* ==========================================================================
 *  Brand mark
 *
 *  The logo is served from each app's own `public/logo.png`, so the path is
 *  `/logo.png` on every origin and no app needs to know where the others live.
 *
 *  It is a raster, not inline SVG, because the artwork carries two metallic
 *  gradients and bevelled edges that do not survive being redrawn as flat
 *  paths. Sized variants are generated at build-prep time — the 256px source
 *  is 30 KB, which is small enough to serve at every display size rather than
 *  shipping a srcset for a mark that never renders above 40px.
 *
 *  `width`/`height` are always set so the header never reflows when the image
 *  lands, and it is marked `decorative` when the wordmark beside it already
 *  supplies the accessible name.
 * ========================================================================== */

export function LogoMark({
  size = 44,
  className = '',
  decorative = true,
}: {
  size?: number;
  className?: string;
  decorative?: boolean;
}) {
  return (
    <img
      src="/logo.png"
      width={size}
      height={size}
      className={`shrink-0 select-none object-contain ${className}`}
      style={{ width: size, height: size }}
      alt={decorative ? '' : 'RiskPay'}
      aria-hidden={decorative || undefined}
      draggable={false}
      // The mark is above the fold in every header — never lazy-load it.
      loading="eager"
      decoding="async"
    />
  );
}

/**
 * Mark plus wordmark, optionally with a portal name beneath.
 *
 * `subtitle` is what distinguishes the five surfaces from one another in the
 * header: the same logo with "Merchant Portal" or "Treasury Core" under it.
 */
export function Wordmark({
  subtitle,
  size = 44,
  className = '',
}: {
  subtitle?: string;
  size?: number;
  className?: string;
}) {
  return (
    <span className={`flex items-center gap-3 ${className}`}>
      <LogoMark size={size} />
      {subtitle ? (
        <span className="flex min-w-0 flex-col leading-none">
          <span className="text-[24px] font-semibold tracking-[-0.026em] text-content">RiskPay</span>
          <span className="mt-1.5 truncate text-xs text-content-subtle">{subtitle}</span>
        </span>
      ) : (
        <span className="text-[24px] font-semibold tracking-[-0.026em] text-content">RiskPay</span>
      )}
    </span>
  );
}
